import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LOCALE, isLocale, type Locale } from "./config";
import { dictionaries } from "./dictionaries";
import type { Dictionary } from "./types";

const STORAGE_KEY = "cate:lang";

/** Infer a locale from the visitor's IANA timezone (a country proxy). */
function localeFromTimeZone(): Locale | null {
  let tz = "";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return null; }
  if (/Shanghai|Chongqing|Urumqi|Harbin|Hong_Kong|Macau|Macao|Taipei/.test(tz)) return "zh";
  if (/^(Europe\/Madrid|Atlantic\/Canary|Africa\/Ceuta|America\/Argentina\/|America\/(Mexico_City|Cancun|Merida|Monterrey|Matamoros|Chihuahua|Ojinaga|Hermosillo|Mazatlan|Bahia_Banderas|Tijuana|Bogota|Santiago|Punta_Arenas|Lima|Caracas|Guayaquil|Guatemala|Havana|La_Paz|Santo_Domingo|Tegucigalpa|Asuncion|El_Salvador|Managua|Costa_Rica|Panama|Montevideo|Puerto_Rico))/.test(tz)) return "es";
  return null;
}

/**
 * Stored choice → country (server geo header, else timezone) → browser
 * language → default. Safe to call during render.
 */
export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored; // an explicit user choice always wins
  } catch {}
  try {
    const geo = (window as any).__CATE_LOCALE__;
    if (isLocale(geo)) return geo; // auto-selected from the visitor's country (server geo)
  } catch {}
  const tz = localeFromTimeZone(); // country fallback when no geo header is present
  if (tz) return tz;
  try {
    const nav = navigator.language?.slice(0, 2).toLowerCase();
    if (isLocale(nav)) return nav;
  } catch {}
  return DEFAULT_LOCALE;
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: Dictionary;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  // Reflect the active language on <html lang> for a11y and crawlers.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t: dictionaries[locale] }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale/useT must be used within a LocaleProvider");
  return ctx;
}

/** `{ locale, setLocale }` — for the language switcher and locale-aware lookups. */
export function useLocale(): { locale: Locale; setLocale: (next: Locale) => void } {
  const { locale, setLocale } = useLocaleContext();
  return { locale, setLocale };
}

/** The active locale's dictionary. */
export function useT(): Dictionary {
  return useLocaleContext().t;
}
